import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { ThemeProvider as FluentThemeProvider, createTheme, Theme } from '@fluentui/react'

type ThemeMode = 'light' | 'dark'

interface ThemeContextType {
  themeMode: ThemeMode
  toggleTheme: () => void
  setThemeMode: (mode: ThemeMode) => void
  isDarkMode: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const THEME_STORAGE_KEY = 'sso-admin-theme'

// Light theme (default FluentUI with slight customizations)
const lightTheme: Theme = createTheme({
  palette: {
    themePrimary: '#0078d4',
    themeLighterAlt: '#eff6fc',
    themeLighter: '#deecf9',
    themeLight: '#c7e0f4',
    themeTertiary: '#71afe5',
    themeSecondary: '#2b88d8',
    themeDarkAlt: '#106ebe',
    themeDark: '#005a9e',
    themeDarker: '#004578',
    neutralLighterAlt: '#faf9f8',
    neutralLighter: '#f3f2f1',
    neutralLight: '#edebe9',
    neutralQuaternaryAlt: '#e1dfdd',
    neutralQuaternary: '#d0d0d0',
    neutralTertiaryAlt: '#c8c6c4',
    neutralTertiary: '#a19f9d',
    neutralSecondary: '#605e5c',
    neutralPrimaryAlt: '#3b3a39',
    neutralPrimary: '#323130',
    neutralDark: '#201f1e',
    black: '#000000',
    white: '#ffffff',
  },
})

// Dark theme
const darkTheme: Theme = createTheme({
  palette: {
    themePrimary: '#4fc3f7',
    themeLighterAlt: '#020809',
    themeLighter: '#082025',
    themeLight: '#0f3d46',
    themeTertiary: '#1e7a8c',
    themeSecondary: '#2cb4cd',
    themeDarkAlt: '#60c9f8',
    themeDark: '#79d1f9',
    themeDarker: '#9dddfb',
    neutralLighterAlt: '#1e1e1e',
    neutralLighter: '#282828',
    neutralLight: '#363636',
    neutralQuaternaryAlt: '#3e3e3e',
    neutralQuaternary: '#454545',
    neutralTertiaryAlt: '#656565',
    neutralTertiary: '#c8c8c8',
    neutralSecondary: '#d0d0d0',
    neutralPrimaryAlt: '#dadada',
    neutralPrimary: '#e5e5e5',
    neutralDark: '#f4f4f4',
    black: '#f8f8f8',
    white: '#121212',
  },
  semanticColors: {
    bodyBackground: '#121212',
    bodyText: '#e5e5e5',
    bodySubtext: '#a0a0a0',
    disabledBackground: '#2d2d2d',
    disabledText: '#6d6d6d',
    errorText: '#ff6b6b',
    inputBackground: '#1e1e1e',
    inputBorder: '#404040',
    inputText: '#e5e5e5',
    link: '#4fc3f7',
    linkHovered: '#81d4fa',
    menuBackground: '#1e1e1e',
    menuItemBackgroundHovered: '#2d2d2d',
  },
})

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    // Check localStorage first
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') {
      return stored
    }
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    return 'light'
  })

  // Apply theme to document for CSS variables
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode)
    localStorage.setItem(THEME_STORAGE_KEY, themeMode)
  }, [themeMode])

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't explicitly set a preference
      const stored = localStorage.getItem(THEME_STORAGE_KEY)
      if (!stored) {
        setThemeModeState(e.matches ? 'dark' : 'light')
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const toggleTheme = () => {
    setThemeModeState(prev => prev === 'light' ? 'dark' : 'light')
  }

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode)
  }

  const theme = themeMode === 'dark' ? darkTheme : lightTheme

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        toggleTheme,
        setThemeMode,
        isDarkMode: themeMode === 'dark',
      }}
    >
      <FluentThemeProvider theme={theme}>
        {children}
      </FluentThemeProvider>
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
